/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-static-element-interactions */
import PropTypes from 'prop-types';
import React, { useEffect, useCallback } from 'react';
import { Field } from 'redux-form';

import cls from 'classnames';

import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

import Button from '@ott/button';
import Datepicker from '@ott/datepicker';
import GeoAutocompleteNext from '@ott/geo-autocomplete-next';
import CrossTinyIcon from '@ott/icon-cross-tiny';
import ReverseIcon from '@ott/icon-reverse';
import { l10n } from '@ott/l10n';
import Tooltip from '@ott/tooltip';

import { KEYS } from '../../helpers/constants';
import { getFormattedRouteFromLocalStorage } from '../../helpers/searchHistoryLocalStorage';
import { usePrev } from './hooks';

import styles from './Flight.scss';

const whenDatepickerChildrenClassNames = {
  container: styles.whenDatepicker,
  input: styles.whenDatepickerInput,
};

const whenDatepickerClassNames = {
  inputTrigger: styles.whenDatepickerInput,
};

const routeDirectionsOptions = [
  { name: l10n('flightsSearchForm.roundTrip'), value: 'roundTrip' },
  { name: l10n('flightsSearchForm.oneWay'), value: 'oneWay' },
];

const now = new Date();
// Минимальная дата - вчера, чтобы покупать билеты из Нью-Йорка в Москву из Москвы
let minDateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
if (new Date().getUTCHours() + 3 >= 9) {
  // Если больше 9 утра по Московскому времени, то минимальная дата - сегодня
  // https://onetwotripdev.atlassian.net/browse/FA-1546
  minDateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate());
}
const maxDateFrom = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate() - 2);

const checkEqualityByNameAndCode = (a, b) => `${a?.name}${a?.code}` === `${b?.name}${b?.code}`;
function Flight({
  index,
  travellersNode,
  heatmap,
  heatmapStatus,
  updateHeatmap,
  onDateSelect,
  onDirectionChanged,
  formName,
  flights,
  member,
  isRT,
  onSuggestDest,
  onDirectionSelect,
  suggests = {},
  onBlurDate,
  onAutocompleteText,
  onGeoAutocompleteFocus,
  onRemoveFlightButtonClick,
  onReverseButtonClick,
  isTouchDevice,
  isSearchForExchange,
  isExchangeReroutingEnable,
  enableInitialFocus,
  submitRef,
  withHeatmap,
}) {
  const {
    [`${formName}.${member}.from`]: fromSuggest = {},
    [`${formName}.${member}.to`]: toSuggest = {},
  } = suggests;

  const flight = flights[index];
  const { from, to } = flight;

  const setSearchForExchangeStyles = () => {
    whenDatepickerChildrenClassNames.input = styles.whenDatepickerInputForExchange;
    whenDatepickerChildrenClassNames.container = styles.whenDatepickerForExchange;

    whenDatepickerClassNames.inputTrigger = styles.whenDatepickerInputForExchange;
  };

  useEffect(() => {
    if (isSearchForExchange) {
      setSearchForExchangeStyles();
    }

    if (!isTouchDevice && enableInitialFocus) {
      // eslint-disable-next-line no-use-before-define
      focusOnFirstEmptyField(flights[index], member, isRT && flights.length === 1);
    }
    // запрос данных о городах при открытии формы
    onSuggestDest(flights[index].from.text, `${member}.from`, member);
    onSuggestDest(flights[index].to.text, `${member}.to`, member);
  }, []);

  const prevSuggests = usePrev(suggests);

  useEffect(() => {
    if (_isEmpty(suggests) || !prevSuggests) {
      return;
    }

    // инициализация полей данными о городе при открытии формы
    const {
      [`${formName}.${member}.from`]: prevFromSuggest = {},
      [`${formName}.${member}.to`]: prevToSuggest = {},
    } = prevSuggests;

    const isCityFromWithoutFullInfo = from.text && !from.cityInfo;
    const hasSuggestFromItems = fromSuggest && fromSuggest.items && fromSuggest.items.length;

    if (
      hasSuggestFromItems &&
      prevFromSuggest &&
      !_isEqual(prevFromSuggest.items, fromSuggest.items) &&
      isCityFromWithoutFullInfo
    ) {
      onDirectionSelect(index, fromSuggest.items[0], `${member}.from`);
    }

    const isCityToWithoutFullInfo = to.text && !to.cityInfo;
    const hasSuggestToItems = toSuggest && toSuggest.items && toSuggest.items.length;

    if (
      hasSuggestToItems &&
      prevToSuggest &&
      !_isEqual(prevToSuggest.items, toSuggest.items) &&
      isCityToWithoutFullInfo
    ) {
      onDirectionSelect(index, toSuggest.items[0], `${member}.to`);
    }
  });

  const handleAutocompleteFrom = useCallback(
    (text) => {
      onAutocompleteText(text, `${member}.from`, index);
    },
    [member, index, onAutocompleteText]
  );

  const handleAutocompleteTo = useCallback(
    (text) => {
      onAutocompleteText(text, `${member}.to`, index);
    },
    [member, index, onAutocompleteText]
  );

  const handleDirectionSelectTo = useCallback(
    (item, options) => {
      if (!item) {
        return;
      }
      onDirectionSelect(index, item, `${member}.to`, options);
    },
    [member, index, onDirectionSelect]
  );

  const handleDirectionSelectFrom = useCallback(
    (item, options) => {
      if (!item) {
        return;
      }
      onDirectionSelect(index, item, `${member}.from`, options);
    },
    [onDirectionSelect, member, index]
  );

  const handleFocusFrom = useCallback(() => {
    onGeoAutocompleteFocus(`${member}.from`, index);
  }, [onGeoAutocompleteFocus, member, index]);

  const handleFocusTo = useCallback(() => {
    onGeoAutocompleteFocus(`${member}.to`, index);
  }, [onGeoAutocompleteFocus, member, index]);

  const handleDateSelect = useCallback(
    (...rest) => {
      onDateSelect(flight, `${member}.dates`, index, ...rest);
    },
    [flight, member, index, onDateSelect]
  );

  const hanleDirectionChange = useCallback(
    (...rest) => {
      onDirectionChanged(flight, `${member}.dates`, index, ...rest);
    },
    [flight, member, onDirectionChanged]
  );

  const handleBlurDate = useCallback(() => {
    onBlurDate(flight);
  }, [onBlurDate, flight]);

  const handleKeyDown = useCallback(
    (event, name) => {
      // автозаполнение по tab и enter
      if (![KEYS.TAB, KEYS.ENTER].includes(event.key)) {
        return;
      }

      const suggest = name === `${member}.from` ? fromSuggest : toSuggest;
      onDirectionSelect(index, suggest.items[0], name);
    },
    [index, member, onDirectionSelect, fromSuggest, toSuggest]
  );

  const renderInput = useCallback((field) => {
    const { input, meta, type, triggerProps, ...fieldProps } = field;

    return (
      <GeoAutocompleteNext
        triggerProps={{
          ...triggerProps,
          // eslint-disable-next-line no-use-before-define
          OverlayInfoRenderer: OverlayNode,
          type,
          initial: meta.initial,
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...fieldProps}
        revertChanges={true}
        withArrow={false}
      />
    );
  }, []);

  const renderDatepicker = useCallback((field) => {
    const {
      input,
      toDatePlaceholder,
      toDateValue,
      fromDateValue,
      childrenClassNames,
      meta,
      ...rest
    } = field;

    childrenClassNames.label = cls({ [styles.datepickerError]: meta.submitFailed && meta.invalid });

    const inputProps = {
      ...input,
      placeholder: toDatePlaceholder,
      value: {
        startDate: input.value.toDate,
        endDate: input.value.fromDate,
      },
    };

    return (
      <Datepicker
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        input={inputProps}
        meta={meta}
        childrenClassNames={childrenClassNames}
      />
    );
  }, []);

  const renderSwitchButton = () => {
    const tooltipContent = (
      <div data-locator="reverse-button-tooltip-text">{l10n('flightsSearchForm.reverse')}</div>
    );

    return (
      <Tooltip
        content={tooltipContent}
        triggerClassName={styles.reverseButtonContainer}
        isDisabledOnTouchDevice={true}
      >
        <div
          className={cls(styles.reverseButton, {
            [styles.isExchangeReroutingEnable]: isExchangeReroutingEnable,
          })}
          data-locator="reverseButton"
          onClick={onReverseButtonClick}
        >
          <ReverseIcon
            className={cls(styles.reverseIcon, {
              [styles['reverseIcon--isExchangeReroutingEnable']]: isExchangeReroutingEnable,
            })}
          />
        </div>
      </Tooltip>
    );
  };

  const renderRemoveFlightButton = () => {
    return (
      <span
        className={styles.removeFlightButton}
        data-locator="removeFlightButton"
        onClick={onRemoveFlightButtonClick.bind(null, index)}
      >
        <CrossTinyIcon w={16} h={16} className={styles.removeFlightIcon} />
      </span>
    );
  };

  let minDate = minDateFrom;

  if (index > 0) {
    const prevToDateValue = flights[index - 1].dates.toDate;
    if (prevToDateValue) {
      minDate = prevToDateValue;
    }
  }

  const isComplexRoute = flights.length > 1;
  const showReverseButton = isSearchForExchange
    ? isSearchForExchange && isExchangeReroutingEnable
    : flights.length === 1;
  const showRouteDirections = !isComplexRoute && !isSearchForExchange;

  const isFirstRow = index === 0;
  const isNeedSelectTwoDate = !isComplexRoute && isRT;

  const renderFromToFields = () => {
    if (isSearchForExchange && !isExchangeReroutingEnable) {
      return null;
    }

    const fromAutocompleteItems = fromSuggest.items || [];
    const toAutocompleteItems = toSuggest.items || [];

    const fromSuggestLoading = fromSuggest.status && fromSuggest.status.loading;
    const toSuggestLoading = toSuggest.status && toSuggest.status.loading;

    return (
      <>
        <div
          className={cls(styles.fromAutocompleteWrapper, {
            [styles.isExchangeReroutingEnable]: isExchangeReroutingEnable,
          })}
        >
          <Field
            name={`${member}.from`}
            component={renderInput}
            overlayProps={{
              title: l10n('flightsSearchForm.from'),
              options: fromAutocompleteItems,
              className: styles.autocompleteDropdown,
              checkEquality: checkEqualityByNameAndCode,
              optionRendererProps: {
                className: styles.optionRenderer,
                iconClassName: styles.optionRendererIcon,
              },
            }}
            triggerProps={{
              name: `${member}.from`,
              value: from.text || '',
              withFullMask: true,
              className: styles.fromAutocomplete,
              placeholder: l10n('flightsSearchForm.from'),
              title: l10n('flightsSearchForm.from'),
              onSearch: handleAutocompleteFrom,
              onFocus: handleFocusFrom,
              onKeyDown: handleKeyDown,
              specificLocators: `GeoAutocomplete--from`,
            }}
            props={{
              value: from,
              className: styles.fromAutocomplete,
              onChange: handleDirectionSelectFrom,
              isLoading: fromSuggestLoading,
            }}
          />
        </div>
        <div
          className={cls(styles.toAutocompleteWrapper, {
            [styles.isExchangeReroutingEnable]: isExchangeReroutingEnable,
          })}
        >
          <Field
            name={`${member}.to`}
            component={renderInput}
            overlayProps={{
              title: l10n('flightsSearchForm.to'),
              options: toAutocompleteItems,
              className: styles.autocompleteDropdown,
              checkEquality: checkEqualityByNameAndCode,
              optionRendererProps: {
                className: styles.optionRenderer,
                iconClassName: styles.optionRendererIcon,
              },
            }}
            triggerProps={{
              name: `${member}.to`,
              value: to.text || '',
              withFullMask: true,
              className: styles.toAutocomplete,
              placeholder: l10n('flightsSearchForm.to'),
              title: l10n('flightsSearchForm.to'),
              onSearch: handleAutocompleteTo,
              onKeyDown: handleKeyDown,
              onFocus: handleFocusTo,
              specificLocators: `GeoAutocomplete--to`,
            }}
            props={{
              value: to,
              className: styles.toAutocomplete,
              onChange: handleDirectionSelectTo,
              isLoading: toSuggestLoading,
            }}
          />
        </div>
      </>
    );
  };

  return (
    <div
      className={cls(styles.container, {
        [styles['container--complexRoute']]: isComplexRoute,
      })}
      data-locator="flightRow"
    >
      {renderFromToFields()}
      {showReverseButton && renderSwitchButton()}
      <Field
        name={`${member}.dates`}
        component={renderDatepicker}
        classNames={whenDatepickerClassNames}
        childrenClassNames={whenDatepickerChildrenClassNames}
        onBlur={handleBlurDate}
        onDateSelect={handleDateSelect}
        pickOneDate={!isNeedSelectTwoDate}
        minDate={minDate}
        maxDate={maxDateFrom}
        calendarPlacement={isComplexRoute ? 'right' : 'left'}
        errorPlacement="top"
        placeholder={l10n('flightsSearchForm.when')}
        title={l10n('flightsSearchForm.when')}
        calendarTheme="heatmap"
        withHeatmap={withHeatmap}
        heatmap={heatmap}
        heatmapStatus={heatmapStatus}
        updateHeatmap={updateHeatmap}
        showHeatmapPrices={true}
        showHeatmapLegend={false}
        // datepickerHeader props
        showRouteDirections={showRouteDirections}
        routeDirectionsOptions={routeDirectionsOptions}
        defaultOptionDirectionIndex={isNeedSelectTwoDate ? 0 : 1}
        onOptionSelect={hanleDirectionChange}
      />

      {!isComplexRoute && travellersNode}
      {!isComplexRoute && (
        <Button
          innerRef={submitRef}
          className={cls(styles.submitButton, {
            [styles.searchForExchangeSubmit]: isSearchForExchange,
          })}
          type="submit"
          data-locator="submitButton"
        >
          {l10n('flightsSearchForm.filters.search')}
        </Button>
      )}
      {!isFirstRow && (
        <Tooltip
          triggerClassName={styles.removeFlightButtonContainer}
          content={<div>{l10n('flightsSearchForm.removeFlight')}</div>}
          isDisabledOnTouchDevice={true}
        >
          {renderRemoveFlightButton()}
        </Tooltip>
      )}
    </div>
  );
}

function OverlayNode({ isFocused, option, isMobileInput }) {
  if (!option || !option.code) {
    return null;
  }

  return (
    <div
      className={cls(styles.code, {
        [styles.hide]: isFocused,
        [styles.withRightPadding]: isMobileInput,
      })}
    >
      {!isFocused && (
        <>
          <div className={styles.gradient} />
          <span className={cls(styles.codeTag)}>{option.code}</span>
        </>
      )}
    </div>
  );
}

export function focusOnFirstEmptyField(flight, member, isRT) {
  const {
    from: { code: fromCode },
    to: { code: toCode },
    dates: { toDate, fromDate },
  } = flight;

  const names = ['from', 'to', 'dates'];
  const values = [fromCode, toCode, toDate];
  const formattedRoute = getFormattedRouteFromLocalStorage();

  if (isRT) {
    names.push('dates');
    values.push(fromDate);
  }

  if (formattedRoute) {
    return;
  }

  const firstEmptyValueIndex = values.findIndex((value) => !value);
  if (firstEmptyValueIndex !== -1) {
    setTimeout(() => {
      const selector = `[name="${member}.${names[firstEmptyValueIndex]}"]`;
      const element = document.querySelector(selector);
      if (element && typeof element.focus === 'function') {
        element.focus();
      }
    }, 0);
  }
}

Flight.propTypes = {
  flights: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.object)).isRequired,
  suggests: PropTypes.objectOf(PropTypes.oneOfType(PropTypes.object)).isRequired,
  member: PropTypes.string.isRequired,
  isRT: PropTypes.bool.isRequired,
  isTouchDevice: PropTypes.bool.isRequired,
  onSuggestDest: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
  onDirectionSelect: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  onGeoAutocompleteFocus: PropTypes.func.isRequired,
  onAutocompleteText: PropTypes.func.isRequired,
  onDateSelect: PropTypes.func.isRequired,
  onBlurDate: PropTypes.func.isRequired,
  onDirectionChanged: PropTypes.func.isRequired,
  onReverseButtonClick: PropTypes.func.isRequired,
  onRemoveFlightButtonClick: PropTypes.func.isRequired,
  onResetSuggest: PropTypes.func.isRequired,
  isSearchForExchange: PropTypes.bool.isRequired,
  isExchangeReroutingEnable: PropTypes.bool.isRequired,
  enableInitialFocus: PropTypes.bool.isRequired,
};

export default React.memo(Flight);
